import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { AgrBrandingModule } from '@next/core-lib/branding';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { AgrNavigationRailModule } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { AgrElasticApiBaseModule } from '@next/core-lib/elastic-base';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { AgrHeaderModule } from '@next/core-lib/header';
import { I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { AgrPWAModule } from '@next/core-lib/pwa';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';

import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import { CycleEffects } from './cycle/store/effects/cycle.effects';
import * as cycleReducer from './cycle/store/reducers/cycle.reducer';
import { DeliveryOverviewResolver } from './delivery/resolvers/delivery-overview.resolver';
import { DeliveryEffects } from './delivery/store/delivery.effects';
import * as deliveryReducer from './delivery/store/delivery.reducer';
import { PerformanceResolver } from './performance/resolvers/performance.resolver';
import { PerformanceEffects } from './performance/store/effects/performance.effects';
import * as performanceReducer from './performance/store/reducers/performance.reducer';
import { ReferenceValueSheetOverviewResolver } from './reference-value-sheet/resolvers/reference-value-sheet-overview.resolver';
import { ReferenceValueSheetEffects } from './reference-value-sheet/store/reference-value-sheet.effects';
import * as referenceValueSheetReducer from './reference-value-sheet/store/reference-value-sheet.reducer';
import { loadedConfig } from './runtime-config';
import { SettingsResolver } from './settings/resolvers/settings.resolver';
import { PoultryEffects } from './settings/store/effects/poultry.effects';
import { SettingEffects } from './settings/store/effects/setting.effects';
import * as settingReducer from './settings/store/reducers/setting.reducer';
import { provideIconInitializer } from './shared/icon-initializer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAgrBrowser(),
    provideAgrCore(),
    provideAgrAdvisorRelationshipsSelector(),
    provideAgrElasticApi(),
    provideAgrI18nShared(),
    provideAgrSettings(),
    provideAgrSite(),
    provideAgrNavigation(),
    provideAgrWrapperSiteSidenav(),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    provideState('cycleModule', cycleReducer.reducer),
    provideState('deliveryModule', deliveryReducer.reducer),
    provideState('performanceModule', performanceReducer.reducer),
    provideState('referenceValueSheetModule', referenceValueSheetReducer.reducer),
    provideState('settingModule', settingReducer.reducer),
    provideAgrRouterStore(),
    provideEffects(
      PoultryEffects,
      CoreEffects,
      I18nEffects,
      I18nBrowserEffects,
      SiteEffects,
      NavigationEffects,
      CycleEffects,
      DeliveryEffects,
      PerformanceEffects,
      SettingEffects,
      ReferenceValueSheetEffects,
    ),
    DeliveryOverviewResolver,
    PerformanceResolver,
    ReferenceValueSheetOverviewResolver,
    SettingsResolver,
    provideIconInitializer(),
    importProvidersFrom(
      AgrBrandingModule,
      AgrHeaderModule,
      AgrNavigationRailModule,
      AgrPWAModule,
      AgrElasticApiBaseModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.PRODUCTION,
      }),
    ),
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
  ],
};
